import { Link } from 'react-router-dom'
import { getErrorMessage } from '@/api/errors'
import { WorkflowStatusBadge } from '@/components/projects/workflows/shared'
import { Pagination } from '@/components/ui/pagination'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { usePagination, useVariableGroupUsage } from '@/hooks'
import { formatDateTime } from '@/lib/timeFormatters'

interface VariableGroupUsageTabProps {
  projectId: string
}

export function VariableGroupUsageTab({ projectId }: VariableGroupUsageTabProps) {
  const { page, pageSize, paginationParams, setPage, setPageSize } = usePagination()
  const { data, isLoading, isError, error } = useVariableGroupUsage(projectId, paginationParams)
  const usages = data?.items ?? []
  const totalCount = data?.totalCount ?? 0

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold">Usage History</h2>
        <p className="text-sm text-muted-foreground">
          Workflow runs that used environment variable groups in this project. Values are never shown.
        </p>
      </div>

      {isLoading ? (
        <div className="space-y-3" role="status" aria-live="polite" aria-label="Loading usage history">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : isError ? (
        <p role="alert" className="rounded border border-destructive px-3 py-2 text-sm text-destructive">
          {getErrorMessage(error) ?? 'Failed to load the usage history.'}
        </p>
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <Table aria-label="Environment variable group usage table">
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="w-[190px]">Date</TableHead>
                <TableHead>Group</TableHead>
                <TableHead className="w-[220px]">Workflow</TableHead>
                <TableHead className="w-[110px]">Run #</TableHead>
                <TableHead className="w-[130px]">Status</TableHead>
                <TableHead className="w-[160px]">Triggered By</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {totalCount === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12 text-muted-foreground" data-testid="variable-group-usage-empty">
                    No workflow run has used an environment variable group yet.
                  </TableCell>
                </TableRow>
              ) : (
                usages.map(usage => (
                  <TableRow key={`${usage.workflowRunId}-${usage.groupId}`}>
                    <TableCell>{formatDateTime(usage.usedAt)}</TableCell>
                    <TableCell>
                      <span className="font-semibold">{usage.groupName}</span>
                      {usage.groupDeleted && <span className="ml-1 text-xs text-muted-foreground">(deleted)</span>}
                    </TableCell>
                    <TableCell>{usage.workflowDisplayName}</TableCell>
                    <TableCell>
                      <Link
                        to={`/projects/${projectId}/workflows/runs/${usage.workflowRunId}`}
                        className="text-info-foreground underline underline-offset-2 hover:text-info-foreground/70"
                      >
                        #{usage.workflowRunId.slice(0, 8)}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <WorkflowStatusBadge status={usage.runStatus} />
                    </TableCell>
                    <TableCell>{usage.triggeredBy}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <Pagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </div>
      )}
    </div>
  )
}
